/**
 * Push reminders on the web (docs/NOTIFICATIONS.md; core push.ts).
 * - PushAskCard: the permission ask, only right after a value moment (core pushAsk, B = MAT): one tap, "Skip reminders"
 *   respected for a week, never a wall in front of the work.
 * - RemindersSection: Profile's device setup (this phone, this browser), with an honest line for what this browser can do
 *   (Safari on iPhone: add to the Home Screen first). Which kinds reach you, and how, is Me → Notifications (one place).
 */
// element: push-ask (journey: turn on reminders at a value moment; registry: none in repo, see report)
// element: reminders-section (journey: choose which reminders reach me, per channel and kind)
import { useState } from "react";
import { pushAsk, pushSupportLine, type PushChannel, type PushSettingsView, type PushSupport, type PushType, type PushValueMoment } from "@logwave/core";
import { dismissPushAsk, enableWebPush, pushDismissedAt, webPushRegistered, webPushSupport } from "../lib/push.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSessionMaybe } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, Card, ErrorBox, LinkButton, Notice, Section, Skeleton } from "./ui.tsx";

const browserRegistered = (s?: PushSettingsView) => !!s && webPushRegistered(s.config.browser.publicKey) && s.devices.some((d) => d.channel === "browser");

/** The ask. Renders nothing unless the moment, the browser and the server all allow a real "yes". */
export function PushAskCard({ moment }: { moment: PushValueMoment }) {
  const api = useApi();
  const settings = useLoad(() => api.pushSettings().catch(() => null), [api]);
  const [state, setState] = useState<"ask" | "on" | "gone" | "blocked">("ask");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const s = settings.data;
  if (!s || !s.config.browser.available || state === "gone") return null;
  if (state === "on") return <Notice tone="go" icon="bell" title="Reminders are on.">Never at night. Change them in Profile.</Notice>;
  if (state === "blocked") return <Notice tone="info" icon="bell">{pushSupportLine("denied", "browser")}</Notice>;
  const ask = pushAsk({ support: webPushSupport(), registered: browserRegistered(s), moment, dismissedAt: pushDismissedAt(), now: new Date().toISOString() });
  if (!ask.show) return null;
  async function turnOn() {
    setBusy(true); setErr(undefined);
    const r = await enableWebPush(api, s!.config.browser.publicKey!);
    setBusy(false);
    if (r.ok) setState("on"); else if (r.support === "denied") setState("blocked"); else if (r.message) setErr(r.message); else { dismissPushAsk(); setState("gone"); }
  }
  return (
    <Card className="stack-sm push-ask">
      <p className="row"><Icon name="bell" size={20} /><strong>{ask.title}</strong></p>
      <p className="small">{ask.body}</p>
      <div className="btn-row">
        <Button icon="bell" busy={busy} onClick={() => void turnOn()}>{ask.button}</Button>
        <Button variant="secondary" onClick={() => { dismissPushAsk(); setState("gone"); }}>Skip reminders</Button>
      </div>
      <ErrorBox error={err} />
    </Card>
  );
}

/** Profile: what this browser can do, and the person's choices. */
export function RemindersSection() {
  const api = useApi();
  // The order-list kind is for managers only (core orderalert.ts O2): nobody else is shown a switch for it.
  const manager = !!useSessionMaybe()?.can("manager");
  const settings = useLoad(() => api.pushSettings(), [api]);
  const [support, setSupport] = useState<PushSupport>(() => webPushSupport());
  const [busy, setBusy] = useState<string>();
  const [err, setErr] = useState<string>();
  const [msg, setMsg] = useState<string>();
  const s = settings.data;
  async function toggle(kind: "channels" | "types", key: PushChannel | PushType, on: boolean) {
    setBusy(key); setErr(undefined); setMsg(undefined);
    try { await api.setPushPrefs({ [kind]: { [key]: on } }); await settings.reload(); setMsg(on ? "Turned on." : "Turned off."); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(undefined); }
  }
  async function turnOn() {
    setBusy("enable"); setErr(undefined);
    const r = await enableWebPush(api, s!.config.browser.publicKey!);
    setSupport(r.support);
    if (r.message) setErr(r.message);
    await settings.reload();
    setBusy(undefined);
  }
  const registered = browserRegistered(s);
  const shown: PushSupport = support === "granted" && !registered ? "default" : support;
  return (
    <Section id="reminders" title="Reminders" kicker="Only your own. Never at night.">
      <Card className="stack-sm reminders">
        {!s ? (settings.error ? <ErrorBox error={settings.error} onRetry={() => void settings.reload()} /> : <Skeleton rows={2} />) : (
          <>
            {s.config.browser.available
              ? <p className="small" role="status">{pushSupportLine(shown, "browser")}</p>
              : <p className="small">Browser reminders aren't set up yet. Phone reminders come with the CleanCleaver app.</p>}
            {s.config.browser.available && shown === "default" && (
              <div><Button icon="bell" busy={busy === "enable"} onClick={() => void turnOn()}>Turn on reminders for this browser</Button></div>
            )}
            <fieldset className="stack-sm">
              <legend className="small">Where</legend>
              {(["phone", "browser"] as const).map((c) => (
                <label key={c} className="check"><input type="checkbox" role="switch" checked={s.prefs.channels[c]} disabled={busy === c} onChange={(e) => void toggle("channels", c, e.target.checked)} /> {c === "phone" ? "My phone (the CleanCleaver app)" : "My browser"}</label>
              ))}
            </fieldset>
            {msg && <p className="small" role="status">{msg}</p>}
            <ErrorBox error={err} />
            {/* Me → Notifications: which kinds, by which channel, for every business and linked email. This section keeps the device setup. */}
            <div><LinkButton to="/me/notifications">Open my notifications</LinkButton></div>
          </>
        )}
      </Card>
    </Section>
  );
}
